<template>
<div class="input-wrapper">

    <label
      for="telephone"
      class="input-wrapper__label"
    >
      {{ ($t('userFormData.telephone')) }} *
    </label>

    <vue-tel-input
      ref="telInput"
      v-model="telephone"
      v-model:defaultCountry="defaultCountry"
      name="telephone"
      required
      type="tel"
      class="input-wrapper__input input-wrapper__input--flex input-wrapper__input--dropdown-position"
      @blur="debouncedInputValidate"
      @input="(e) => {debouncedInputValidate($emit('update:modelValue', e.target.value))}"
      :class="{ 'invalid-input': errorObj.telephone }"
      v-bind="bindProps"
      @change="onCountryChanged"
      @open="onDropdownOpen"
    >
    </vue-tel-input>

    <div
        v-if="errorObj.telephone"
        class="input-wrapper__label input-wrapper__label--error"
    >
        {{ $t(errorObj.telephone) }}
    </div>
</div>
</template>

<script>
import { ref, reactive, onMounted, watch } from 'vue'
import useSignup from '@/composables/useSignup'
import { VueTelInput } from 'vue-tel-input'
import { useI18n } from 'vue-i18n'
import { debounce } from 'lodash'
import axios from 'axios'

export default {
  components: {
    VueTelInput
  },

  setup () {
    const { t } = useI18n()
    const telephone = ref('')
    const telInput = ref(null)
    const defaultCountry = ref(null)
    const activeCountry = reactive({})

    const bindProps = reactive(
      {
        autoDefaultCountry: false,
        onlyCountries: [
          'AE', 'AD', 'AU', 'AT', 'BY', 'BE', 'BA', 'BR', 'BG', 'CA', 'CN', 'HR',
          'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IN', 'ID', 'IE',
          'IT', 'LV', 'LT', 'LU', 'MX', 'NL', 'PK', 'PL', 'PT', 'RO', 'SM',
          'RS', 'SG', 'SK', 'SI', 'ZA', 'KR', 'ES', 'SE', 'CH', 'TW', 'TR',
          'UA', 'GB', 'US'
        ],
        autoFormat: true,
        required: true,
        enabledCountryCode: true,
        enabledFlags: true,
        ignoredCountries: [],
        autocomplete: 'off',
        validCharactersOnly: true,
        preferredCountries: [],
        dropdownOptions: {
          showSearchBox: true,
          showDialCodeInList: true,
          showDialCodeInSelection: false,
          showFlags: true,
          width: '100%'
        },
        inputOptions: {
          autocomplete: false,
          showDialCode: true,
          autofocus: false,
          maxlength: 40
        }
      }
    )

    // return values 'errorObj, nameFieldsValidate' from useSignup
    const { errorObj, telInputValidate } = useSignup(t)

    // methods
    const inputValidate = () => {
      telInputValidate('telephone', telephone.value)
    }

    const debouncedInputValidate = debounce(inputValidate, 500)

    // method that fires after user changed a country and sets focus
    // TODO: vue-tel-input has his own country-change event, but it fires onload and side-effect is: cursor is on telInput ny default
    const onCountryChanged = () => {
      if (telInput.value.phoneObject.nationalNumber || telInput.value.defaultCountry !== telInput.value.activeCountryCode) {
        telInput.value.focus()
      }
    }

    // setting country based on ipapi
    onMounted(async () => {
      try {
        const { data } = await axios.get('https://ipapi.co/json?fields=country_code')
        const countryCode = data.country_code
        const defaultCountryIndex = telInput.value.allCountries.findIndex(country => country.iso2 === countryCode)
        if (defaultCountryIndex !== -1) {
          const defaultCountry = telInput.value.allCountries[defaultCountryIndex]
          telInput.value.allCountries.splice(defaultCountryIndex, 1)
          telInput.value.allCountries.unshift(defaultCountry)
          telInput.value.activeCountryCode = defaultCountry.iso2
          telInput.value.phone = `+${defaultCountry.dialCode}`
        }
      } catch (error) {
        console.error(error)
      }
    })

    // setting a placeholder when user delete a whole number in input
    watch(telInput, (newValue) => {
      newValue.parsedPlaceholder = 'Type your telephone number'
    })

    // setting a placeholder to 'Search' for dropdown-search input
    const onDropdownOpen = () => {
      setTimeout(() => {
        const inputs = Array.from(document.getElementsByClassName('vti__search_box'))
        inputs.forEach((searchInput) => {
          searchInput.setAttribute('placeholder', 'Search')
        })
      }, 10)
    }

    return {
      telephone, defaultCountry, debouncedInputValidate, inputValidate, bindProps, telInput, activeCountry, onCountryChanged, onDropdownOpen, t, errorObj
    }
  }
}
</script>
<style lang="scss" scoped>
.input-wrapper {
  position: relative;
  &__input {
    @include input;
    padding: 0;
    padding-left: 15px;

    &--flex {
      display: flex;
    }

    ::v-deep .vti__dropdown {
      padding: 0;
      .vti__dropdown-list {
        left: -16px;
        top: -280px;
        overflow-x: hidden;
        border: 1px solid var(--white0);
        max-height: 278px;
        height: 100vh;
        overflow-y: scroll;
        width: 100vw;
        z-index: 1000;
        background-color: rgb(255, 255, 255);

        .vti__search_box {
          padding-top: var(--spacing-s);
          padding-bottom: var(--spacing-s);
          width: 99%;
          border: none;
          border-bottom: 1px solid var(--grey200);
        }
        .vti__dropdown-item {
          padding-top: var(--spacing-s);
          padding-bottom: var(--spacing-s);

          .vti__flag {
            margin-right: 10px;
          }
        }

        @include respond-to('small') {
          left: -17px;
          top: unset;
          bottom: 45px;
          border: none;
          max-height: 325px;
          height: 100vh;
          width: 100vw;
          max-width: 533px;
          overflow-x: hidden;

          .vti__search_box {
            width: calc(100% - 5px);
            margin-bottom: 3px;
            border: none;
            border-bottom: 1px solid var(--grey200);
          }
        }
      }
    }

    ::v-deep .vti__flag {
      margin-left: 0;
    }

    ::v-deep .vti__input {
      background-color: transparent;
      line-height: normal;
      padding-left: 20px;
    }

    &:focus {
        @include input-focus;
    }
  }
  &__label {
    text-align: left;

    @include input-label;

    &--error {
      @include error-message;
      &::after {
        content: '';
        width: var(--spacing-m);
        height: var(--spacing-m);
        position: absolute;
        top: 36px;
        right: 12px;
        background-image: url('../assets/images/exclamation-circle.svg');
        background-position: center center;
        background-repeat: no-repeat;
        z-index: -1;

        @include respond-to('small') {
          top: 40px;
        }
      }
    }
  }
  .invalid-input {
    @include invalid-input;
  }

  @include shake;

}
</style>
